import { keyframes } from "@emotion/react";
import { Avatar, Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Marker, useMap } from "react-map-gl/mapbox";

import { useStoreBranding } from "@/hooks/useStoreBranding";
import type { Store } from "@/services/features/StoreService";
import { formatInteger, formatSignedDecimal } from "@/utils/number";

import type { StoreHealthCache } from "./commandCenter.types";
import {
  buildStoreStacks,
  createStoreClusterIndex,
  getClusterPetalOffsets,
  getStackOffsets,
  matchClusterMarkers,
  SELECTED_STORE_ZOOM,
} from "./commandCenterClustering";
import { getMonitoringHealthStatus } from "./commandCenterHealth";

export type StackInteraction = {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onFocus: () => void;
  onBlur: () => void;
};

type Props = {
  stores: Store[];
  selectedStoreId: number | null;
  healthByStoreId: Record<number, StoreHealthCache>;
  expanded: boolean;
  renderStore: (store: Store, interaction?: StackInteraction) => React.ReactNode;
  customerNameById?: ReadonlyMap<number, string>;
};

const CLUSTER_TRANSITION_MS = 200;
const markerFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

type MarkerGroup = {
  key: string;
  longitude: number;
  latitude: number;
  stores: Store[];
  storeIds: number[];
  stackId: number | null;
  expansionZoom: number | null;
};

type ClusterTransition = {
  entering: Map<string, MarkerGroup>;
  leaving: { group: MarkerGroup; target: MarkerGroup }[];
};

export const CommandCenterMapMarkers: React.FC<Props> = ({
  stores,
  selectedStoreId,
  healthByStoreId,
  expanded,
  renderStore,
  customerNameById,
}) => {
  const { current: map } = useMap();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const { getStoreBranding } = useStoreBranding();
  const stacks = React.useMemo(() => buildStoreStacks(stores), [stores]);
  const stacksById = React.useMemo(
    () => new globalThis.Map(stacks.map((s) => [s.id, s])),
    [stacks],
  );
  const index = React.useMemo(() => createStoreClusterIndex(stacks), [stacks]);
  const selectedStack = React.useMemo(
    () => stacks.find((stack) => stack.stores.some((store) => store.id === selectedStoreId)),
    [stacks, selectedStoreId],
  );
  const [viewport, setViewport] = React.useState<{
    bounds: [number, number, number, number];
    zoom: number;
  }>({
    bounds: [-180, -90, 180, 90],
    zoom: 2,
  });
  const [clusterTransition, setClusterTransition] = React.useState<ClusterTransition | null>(null);
  const [hoveredGroupKey, setHoveredGroupKey] = React.useState<string | null>(null);
  const [focusedGroupKey, setFocusedGroupKey] = React.useState<string | null>(null);
  const [openStackId, setOpenStackId] = React.useState<number | null>(null);
  const [isClosing, setIsClosing] = React.useState(false);
  const fanTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectionPending = React.useRef(false);
  const keepOpen = React.useCallback(() => {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (fanTimer.current !== null) clearTimeout(fanTimer.current);
    fanTimer.current = null;
    setIsClosing(false);
  }, []);
  const closeSoon = React.useCallback(() => {
    keepOpen();
    closeTimer.current = setTimeout(() => {
      setIsClosing(true);
      fanTimer.current = setTimeout(() => {
        setOpenStackId(null);
        setIsClosing(false);
      }, 180);
    }, 180);
  }, [keepOpen]);
  React.useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
      if (fanTimer.current !== null) clearTimeout(fanTimer.current);
    },
    [],
  );
  React.useEffect(() => {
    keepOpen();
    selectionPending.current = selectedStoreId !== null;
    setOpenStackId(selectedStack && selectedStack.stores.length > 1 ? selectedStack.id : null);
  }, [selectedStack, selectedStoreId, keepOpen]);
  React.useEffect(() => {
    if (!map) return;
    let frame: number | null = null;
    const updateViewport = (commitZoom: boolean) => {
      const canvas = map.getCanvas();
      const northWest = map.unproject([-160, -160]);
      const southEast = map.unproject([canvas.clientWidth + 160, canvas.clientHeight + 160]);
      const next = {
        bounds: [
          northWest.lng,
          Math.max(-90, southEast.lat),
          southEast.lng,
          Math.min(90, northWest.lat),
        ] as [number, number, number, number],
        zoom: Math.ceil(map.getZoom()),
      };
      setViewport((current) => {
        // Follow the visible area during movement, but regroup only at the final zoom.
        const zoom = commitZoom ? next.zoom : current.zoom;
        return current.zoom === zoom &&
          current.bounds.every((value, index) => value === next.bounds[index])
          ? current
          : { bounds: next.bounds, zoom };
      });
    };
    const update = () => updateViewport(false);
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };
    const onMoveEnd = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      updateViewport(true);
      // Initial and resize updates can run before the selection-driven flyTo starts.
      if (selectionPending.current && map.getZoom() >= SELECTED_STORE_ZOOM) {
        const cluster = selectedStack
          ? index
              .getClusters([-180, -90, 180, 90], Math.ceil(map.getZoom()))
              .find(
                (feature) =>
                  "cluster" in feature.properties &&
                  index
                    .getLeaves(feature.properties.cluster_id, Infinity)
                    .some((leaf) => leaf.properties.stackId === selectedStack.id),
              )
          : undefined;
        if (selectedStack && cluster && "cluster" in cluster.properties) {
          // A cluster can split into smaller clusters; keep expanding until the stack is visible.
          map.easeTo({
            center: [selectedStack.longitude, selectedStack.latitude],
            zoom: index.getClusterExpansionZoom(cluster.properties.cluster_id),
          });
          return;
        }
        setOpenStackId(selectedStack && selectedStack.stores.length > 1 ? selectedStack.id : null);
        selectionPending.current = false;
      }
    };
    const close = () => {
      setHoveredGroupKey(null);
      setClusterTransition(null);
      keepOpen();
      setOpenStackId(null);
    };
    updateViewport(true);
    map.on("move", scheduleUpdate);
    map.on("moveend", onMoveEnd);
    map.on("resize", update);
    map.on("movestart", close);
    map.on("click", close);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      map.off("move", scheduleUpdate);
      map.off("moveend", onMoveEnd);
      map.off("resize", update);
      map.off("movestart", close);
      map.off("click", close);
    };
  }, [map, index, selectedStack, keepOpen]);

  const groups = React.useMemo(() => {
    return index.getClusters(viewport.bounds, viewport.zoom).flatMap((feature): MarkerGroup[] => {
      const properties = feature.properties;
      const isCluster = "cluster" in properties;
      const stackId = "stackId" in properties ? properties.stackId : null;
      const stores = isCluster
        ? index
            .getLeaves(properties.cluster_id, Infinity)
            .flatMap((leaf) => stacksById.get(leaf.properties.stackId)?.stores ?? [])
        : stackId === null
          ? []
          : (stacksById.get(stackId)?.stores ?? []);
      if (!stores.length) return [];
      return [
        {
          key: isCluster ? `cluster-${properties.cluster_id}` : `stack-${stackId}`,
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          stores,
          storeIds: stores.map((store) => store.id),
          stackId,
          expansionZoom: isCluster ? index.getClusterExpansionZoom(properties.cluster_id) : null,
        },
      ];
    });
  }, [index, stacksById, viewport]);
  const previousView = React.useRef({ groups, zoom: viewport.zoom, stacks });
  React.useLayoutEffect(() => {
    const previous = previousView.current;
    previousView.current = { groups, zoom: viewport.zoom, stacks };
    // Data changes reset regrouping; panning uses the marker's own entry fade.
    if (previous.stacks !== stacks) {
      setClusterTransition(null);
      return;
    }
    const currentKeys = new Set(groups.map((group) => group.key));
    const previousKeys = new Set(previous.groups.map((group) => group.key));
    // Camera frames with unchanged groups must not interrupt an active animation.
    if (
      currentKeys.size === previousKeys.size &&
      groups.every((group) => previousKeys.has(group.key))
    )
      return;
    if (previous.zoom === viewport.zoom) {
      setClusterTransition(null);
      return;
    }
    const entering = matchClusterMarkers(
      previous.groups,
      groups.filter((group) => !previousKeys.has(group.key)),
    );
    const targets = matchClusterMarkers(
      groups,
      previous.groups.filter((group) => !currentKeys.has(group.key)),
    );
    const leaving = previous.groups.flatMap((group) => {
      const target = targets.get(group.key);
      return target ? [{ group, target }] : [];
    });
    setClusterTransition(entering.size || leaving.length ? { entering, leaving } : null);
  }, [groups, stacks, viewport.zoom]);
  React.useEffect(() => {
    if (!clusterTransition) return;
    const timer = setTimeout(() => setClusterTransition(null), CLUSTER_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [clusterTransition]);
  const markerSize = expanded ? 32 : 24;
  const interaction: StackInteraction = {
    onMouseEnter: keepOpen,
    onMouseLeave: closeSoon,
    onFocus: keepOpen,
    onBlur: closeSoon,
  };

  const animatedContent = (
    group: MarkerGroup,
    target: MarkerGroup | undefined,
    content: React.ReactNode,
  ) => {
    const source = clusterTransition?.entering.get(group.key);
    const other = target ?? source;
    let animation;
    if (other && map) {
      const here = map.project([group.longitude, group.latitude]);
      const there = map.project([other.longitude, other.latitude]);
      const dx = there.x - here.x;
      const dy =
        there.y -
        here.y +
        (group.stores.length === 1 ? markerSize / 2 : 0) -
        (other.stores.length === 1 ? markerSize / 2 : 0);
      animation = keyframes`
        from {
          /* @noflip */ transform: translate(${target ? 0 : dx}px, ${target ? 0 : dy}px) scale(${target ? 1 : 0.85});
          opacity: ${target ? 1 : 0};
        }
        to {
          /* @noflip */ transform: translate(${target ? dx : 0}px, ${target ? dy : 0}px) scale(${target ? 0.85 : 1});
          opacity: ${target ? 0 : 1};
        }
      `;
    }
    return (
      <Box
        data-testid="map-marker-transition"
        data-phase={target ? "leaving" : source ? "entering" : "stable"}
        aria-hidden={target ? true : undefined}
        inert={target ? true : undefined}
        onMouseEnter={target ? undefined : () => setHoveredGroupKey(group.key)}
        onMouseLeave={target ? undefined : () => setHoveredGroupKey(null)}
        onFocusCapture={target ? undefined : () => setFocusedGroupKey(group.key)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusedGroupKey(null);
        }}
        sx={{
          isolation: "isolate",
          pointerEvents: target ? "none" : undefined,
          animation: animation
            ? `${animation} ${CLUSTER_TRANSITION_MS}ms ease-out both`
            : undefined,
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            display: target ? "none" : undefined,
          },
        }}
      >
        <Box
          sx={{
            animation: `${markerFadeIn} ${CLUSTER_TRANSITION_MS}ms ease-out both`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {content}
        </Box>
      </Box>
    );
  };

  const renderedGroups = [
    ...groups.map((group) => ({ group, target: undefined as MarkerGroup | undefined })),
    ...(clusterTransition?.leaving ?? []),
  ].sort((a, b) => (a.group.key < b.group.key ? -1 : a.group.key > b.group.key ? 1 : 0));
  const layerStride = renderedGroups.length + 1;
  return renderedGroups.map(({ group, target }, rank) => {
    const { longitude, latitude, stores: members, stackId, expansionZoom } = group;
    const isCluster = expansionZoom !== null;
    const isOpen = !target && stackId !== null && stackId === openStackId;
    // Separate priority bands keep every marker ordered, including outgoing copies.
    const priority = target
      ? 0
      : isOpen
        ? 5
        : group.key === hoveredGroupKey
          ? 4
          : group.key === focusedGroupKey
            ? 3
            : group.storeIds.includes(selectedStoreId ?? -1)
              ? 2
              : 1;
    const markerStyle: React.CSSProperties = {
      isolation: "isolate",
      zIndex: priority * layerStride + rank + 1,
      pointerEvents: target ? "none" : undefined,
    };
    if (!isCluster && members.length === 1) {
      return (
        <Marker
          key={group.key}
          longitude={longitude}
          latitude={latitude}
          anchor="bottom"
          style={markerStyle}
        >
          {animatedContent(group, target, renderStore(members[0]))}
        </Marker>
      );
    }
    const isDimmed =
      selectedStoreId !== null && !members.some((store) => store.id === selectedStoreId);
    const statuses = members.map((store) =>
      getMonitoringHealthStatus(healthByStoreId[store.id]?.health),
    );
    const status = statuses.includes("unhealthy")
      ? "unhealthy"
      : statuses.includes("toWatch")
        ? "toWatch"
        : null;
    const customers = [...new Set(members.map((store) => store.customerId))]
      .map((id) => ({
        id,
        name:
          customerNameById?.get(id) ||
          members.find((store) => store.customerId === id)?.customerName ||
          t("commandCenter.notAvailable"),
        count: members.filter((store) => store.customerId === id).length,
      }))
      .sort((a, b) => b.count - a.count || a.id - b.id);
    const brands = customers.map((customer) => customer.id).sort((a, b) => a - b);
    const isRosette = isCluster && customers.length > 1;
    if (!isCluster && brands.length === 1) brands.push(brands[0]);
    const size = markerSize;
    const centerSize = expanded ? 12 : 10;
    const stride = size * 0.28;
    const width = isRosette ? size * 2.2 : size + (brands.length - 1) * stride;
    const height = isRosette ? width : size;
    const petals = customers.length > 6 ? customers.slice(0, 5) : customers;
    const hiddenCustomers = customers.length - petals.length;
    const petalOffsets = getClusterPetalOffsets(petals.length + (hiddenCustomers ? 1 : 0), size);
    const activeFanLayer = (brands.length + 1) * (members.length + 1) + 2;
    const offsets = isOpen ? getStackOffsets(members.length, markerSize) : [];
    const radius = Math.max(size / 2, ...offsets.map(([x, y]) => Math.hypot(x, y))) + markerSize;
    // Keep the fan inside the canvas near map edges. Offsets are physical map pixels in both directions.
    const center = map?.project([longitude, latitude]);
    const canvas = map?.getCanvas();
    const shiftX =
      center && canvas && isOpen
        ? Math.max(radius - center.x, Math.min(0, canvas.clientWidth - radius - center.x))
        : 0;
    const shiftY =
      center && canvas && isOpen
        ? Math.max(radius - center.y, Math.min(0, canvas.clientHeight - radius - center.y))
        : 0;
    const label = t(isCluster ? "commandCenter.map.zoomCluster" : "commandCenter.map.expandStack", {
      count: members.length,
    });
    const open = () => {
      keepOpen();
      setOpenStackId(stackId);
    };
    return (
      <Marker
        key={group.key}
        longitude={longitude}
        latitude={latitude}
        anchor="center"
        style={markerStyle}
      >
        {animatedContent(
          group,
          target,
          <Box
            data-testid={isCluster ? "map-cluster" : "map-stack"}
            onMouseEnter={isCluster ? undefined : open}
            onMouseLeave={isCluster ? undefined : closeSoon}
            onFocusCapture={isCluster ? undefined : keepOpen}
            onBlurCapture={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) closeSoon();
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                event.currentTarget.querySelector<HTMLButtonElement>("button")?.focus();
                setOpenStackId(null);
              }
            }}
            onClick={(event) => event.stopPropagation()}
            sx={{ position: "relative", isolation: "isolate", width, height }}
          >
            {isOpen && (
              <Box
                aria-hidden="true"
                style={{ left: width / 2 - radius + shiftX, top: size / 2 - radius + shiftY }}
                sx={{
                  position: "absolute",
                  width: radius * 2,
                  height: radius * 2,
                  borderRadius: "50%",
                  zIndex: 0,
                }}
              />
            )}
            <Tooltip
              describeChild
              title={
                isCluster ? (
                  <Stack spacing={0.75} sx={{ maxHeight: 280, overflowY: "auto" }}>
                    {customers.map((customer) => (
                      <Stack key={customer.id} direction="row" spacing={1} alignItems="center">
                        <Avatar
                          src={getStoreBranding(customer.id).avatar}
                          alt=""
                          sx={{
                            width: 20,
                            height: 20,
                            "& .MuiAvatar-img": { objectFit: "contain" },
                          }}
                        />
                        <Typography variant="caption" sx={{ flex: 1 }}>
                          {customer.name}
                        </Typography>
                        <Typography variant="caption">
                          {formatInteger(customer.count, locale)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  ""
                )
              }
            >
              <Box
                component="button"
                type="button"
                aria-label={label}
                aria-expanded={isCluster ? undefined : isOpen}
                onFocus={isCluster ? undefined : open}
                onClick={() => {
                  if (isCluster) {
                    map?.easeTo({
                      center: [longitude, latitude],
                      zoom: expansionZoom,
                      duration: 500,
                    });
                  } else open();
                }}
                sx={{
                  position: "relative",
                  isolation: "isolate",
                  zIndex: 1,
                  width,
                  height,
                  p: 0,
                  border: 0,
                  borderRadius: "50%",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  opacity: isDimmed ? 0.5 : 1,
                  "&:hover .command-center-group-disc": { boxShadow: 3 },
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 4,
                  },
                }}
              >
                {isOpen ? (
                  <Box
                    component="span"
                    data-testid="map-stack-center"
                    sx={{
                      position: "absolute",
                      left: (width - centerSize) / 2,
                      top: (size - centerSize) / 2,
                      width: centerSize,
                      height: centerSize,
                      borderRadius: "50%",
                      bgcolor: "transparent",
                      border: "1px solid",
                      borderColor: "primary.main",
                      animation: `${keyframes(
                        isClosing
                          ? {
                              from: { transform: "scale(1)", opacity: 1 },
                              to: { transform: "scale(0.35)", opacity: 0 },
                            }
                          : {
                              from: { transform: "scale(0.35)", opacity: 0 },
                              to: { transform: "scale(1)", opacity: 1 },
                            },
                      )} 180ms ease-out both`,
                      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    }}
                  />
                ) : isRosette ? (
                  <>
                    {petalOffsets.map(([x, y], i) => {
                      const customer = petals[i];
                      const branding = customer ? getStoreBranding(customer.id) : undefined;
                      return (
                        <Avatar
                          key={customer?.id ?? "overflow"}
                          data-testid="map-cluster-petal"
                          data-customer-id={customer?.id}
                          className="command-center-group-disc"
                          src={branding?.avatar}
                          alt=""
                          style={{
                            left: width / 2 + x - size * 0.4,
                            top: height / 2 + y - size * 0.4,
                          }}
                          sx={{
                            position: "absolute",
                            width: size * 0.8,
                            height: size * 0.8,
                            bgcolor: "background.paper",
                            color: "text.secondary",
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: 1,
                            fontSize: 10,
                            filter: isDimmed ? "grayscale(1)" : "none",
                            "& .MuiAvatar-img": { objectFit: "contain" },
                          }}
                        >
                          {customer
                            ? customer.name.slice(0, 1)
                            : formatSignedDecimal(hiddenCustomers, locale, {
                                maximumFractionDigits: 0,
                              })}
                        </Avatar>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {brands.map((brandId, i) => {
                      const branding = getStoreBranding(brandId);
                      return (
                        <Avatar
                          key={`${brandId}-${i}`}
                          className="command-center-group-disc"
                          src={branding.avatar}
                          alt=""
                          style={{ left: i * stride, top: 0 }}
                          sx={{
                            position: "absolute",
                            width: size,
                            height: size,
                            bgcolor: "background.paper",
                            color: "text.secondary",
                            border: `1px solid ${branding.color}`,
                            boxShadow: 1,
                            zIndex: brands.length - i,
                            filter: isDimmed ? "grayscale(1)" : "none",
                            "& .MuiAvatar-img": { objectFit: "contain" },
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isCluster && (
                  <Box
                    data-testid="map-cluster-count"
                    component="span"
                    // Physical badge placement is intentional on the map, including in RTL.
                    style={
                      isRosette
                        ? { left: width / 2, top: height / 2, transform: "translate(-50%, -50%)" }
                        : { right: -6, top: -7 }
                    }
                    sx={{
                      position: "absolute",
                      minWidth: isRosette ? size : 17,
                      height: isRosette ? size : 17,
                      px: isRosette ? 0.375 : 0.25,
                      borderRadius: "50%",
                      bgcolor: isRosette ? "primary.main" : getStoreBranding(brands[0]).color,
                      color: isRosette
                        ? "primary.contrastText"
                        : theme.palette.getContrastText(getStoreBranding(brands[0]).color),
                      border: isRosette ? "2px solid" : undefined,
                      borderColor: "background.paper",
                      boxShadow: isRosette ? 1 : undefined,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: isRosette ? (expanded ? 12 : 11) : 10,
                      fontWeight: 700,
                      lineHeight: 1,
                      zIndex: brands.length + 1,
                    }}
                  >
                    {formatInteger(members.length, locale)}
                  </Box>
                )}
                {status && !isOpen && (
                  <Box
                    component="span"
                    role="img"
                    aria-label={t(`commandCenter.status.${status}`)}
                    sx={{
                      position: "absolute",
                      insetInlineEnd: -3,
                      bottom: -3,
                      width: 10,
                      height: 10,
                      bgcolor: status === "unhealthy" ? "error.main" : "warning.main",
                      border: "2px solid",
                      borderColor: "background.paper",
                      borderRadius: status === "unhealthy" ? "50%" : "2px",
                      zIndex: brands.length + 1,
                    }}
                  />
                )}
              </Box>
            </Tooltip>
            {isOpen && (
              <>
                {members.map((store, i) => {
                  const collapsed = `translate(calc(-50% + ${brands.indexOf(store.customerId) * stride + size / 2 - width / 2 - offsets[i][0] - shiftX}px), calc(-50% - ${offsets[i][1] + shiftY}px))`;
                  const fanned = "translate(-50%, -50%)";
                  const animation = keyframes`
                  from { /* @noflip */ transform: ${isClosing ? fanned : collapsed}; }
                  to { /* @noflip */ transform: ${isClosing ? collapsed : fanned}; }
                `;
                  return (
                    <Box
                      key={store.id}
                      data-testid="map-stack-store"
                      // Map projection coordinates must bypass RTL style mirroring.
                      style={{
                        left: width / 2 + offsets[i][0] + shiftX,
                        top: size / 2 + offsets[i][1] + shiftY,
                        transform: fanned,
                      }}
                      sx={{
                        position: "absolute",
                        animation: `${animation} 180ms cubic-bezier(0.2, 0.7, 0.3, 1) both`,
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                        // Match the collapsed customer-disc order throughout both fan animations.
                        zIndex:
                          2 +
                          (brands.length - brands.indexOf(store.customerId)) *
                            (members.length + 1) +
                          members.length -
                          i,
                        "&:focus-within": { zIndex: activeFanLayer },
                        "&:hover": { zIndex: activeFanLayer + 1 },
                      }}
                    >
                      {renderStore(store, interaction)}
                    </Box>
                  );
                })}
              </>
            )}
          </Box>,
        )}
      </Marker>
    );
  });
};
